import { useCallback } from 'react';
import { RenderIf, Spinner } from 'react-rainbow-components';
import { hideAppSpinner, showAppMessage, showAppSpinner } from '@rainbow-modules/app';
import useExternalAutomation from 'data/firestore/agent/externalautomation/useOnce';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { BaseFormProps } from './types';
import FormFooter from './footer';
import Form from './form';
import { Container, Content } from './styled';

const mapDataToFormValues = (
    data: Record<string, unknown>,
) => {
    const {
        name,
        description,
        platform,
    } = data;

    return {
        name,
        description,
        platform,
    } as Record<string, unknown>;
};

interface EditFormProps extends BaseFormProps {}

const EditForm = ({
    onRequestClose = () => {},
    onCompleted = () => {},
    agentId = '',
    ...externalAutomationInfo
}: EditFormProps) => {
    const { data, isLoading } = useExternalAutomation(
        agentId as string,
        externalAutomationInfo?.id as string,
        { disabled: !(agentId || externalAutomationInfo?.id) },
    );

    const externalAutomationData = (agentId ? data : externalAutomationInfo);

    const {
        mutateAsync: updateExternalAutomation,
    } = useHttpMutation<Record<string, unknown>, void>({
        pathname: `/agents/${agentId}/external-automations/${externalAutomationInfo?.id}`,
        method: 'PATCH',
        onSuccess: (response) => {
            onRequestClose();
            onCompleted(response as unknown as Record<string, unknown>);
        },
    });

    const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
        try {
            showAppSpinner();
            await updateExternalAutomation({
                body: values,
            });
        } catch (error) {
            // no catch
            showAppMessage({
                variant: 'error',
                message: (error as Error).message,
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [updateExternalAutomation]);

    return (
        <Container>
            <Content>
                <RenderIf isTrue={isLoading}>
                    <Spinner size="large" variant="brand" type="arc" />
                </RenderIf>
                <RenderIf isTrue={!isLoading}>
                    <Form
                        id="external-automation-form"
                        initialValues={mapDataToFormValues(
                            externalAutomationData as Record<string, unknown> || {},
                        )}
                        onSubmit={handleSubmit}
                    />
                </RenderIf>
            </Content>
            <FormFooter
                formId="external-automation-form"
                submitDisabled={isLoading}
                submitButtonLabel="Update"
                onCancelClick={onRequestClose}
            />
        </Container>
    );
};

export default EditForm;
